import { AggregationSurvey } from "./AggregationSurvey";
import { SurveyWeightingItem } from "@/hooks/configuration";
import type { DatasetBase } from "./DatasetBase";
import type { DatasetMetric } from "./Metric";

// ─── Date Guardrails ──────────────────────────────────────────────────────────

export type TimeSeriesUnit = "week" | "month" | "quarter" | "semester";
export type PointInTimeUnit = "month" | "quarter" | "semester";

/**
 * 0 = "No Rolling Average"; 2/3/4/6/8/12/18 = N Periods.
 * Used in the guardrail allow-list and default fields.
 */
export type GuardrailRollingOption = 0 | 2 | 3 | 4 | 6 | 8 | 12 | 18;

export interface UnitGuardrail {
  enabled: boolean;
  /** At least one option required when unit is enabled. */
  allowedRollingAverages: GuardrailRollingOption[];
  /** Must be one of allowedRollingAverages. */
  defaultRollingAverage: GuardrailRollingOption;
}

export interface DateGuardrails {
  timeSeries: {
    units: Record<TimeSeriesUnit, UnitGuardrail>;
    /** Must be an enabled unit. Required when at least one unit is enabled. */
    defaultUnit: TimeSeriesUnit;
  };
  pointInTime: {
    units: Record<PointInTimeUnit, UnitGuardrail>;
  };
}

/** Rolling average options available per time-series unit (PRD Module 1). */
export const TS_GUARDRAIL_ROLLING_OPTIONS: Record<TimeSeriesUnit, GuardrailRollingOption[]> = {
  week:     [0, 2, 4, 8, 12],
  month:    [0, 2, 3, 6, 12],
  quarter:  [0, 2, 3, 4],
  semester: [0, 2, 3],
};

/** Rolling average options available per point-in-time unit (PRD Module 1). */
export const PIT_GUARDRAIL_ROLLING_OPTIONS: Record<PointInTimeUnit, GuardrailRollingOption[]> = {
  month:    [0, 2, 3, 6, 12, 18],
  quarter:  [0, 2, 3, 4],
  semester: [0, 2, 3, 4],
};

/**
 * @deprecated Use TS_GUARDRAIL_ROLLING_OPTIONS or PIT_GUARDRAIL_ROLLING_OPTIONS.
 * Kept temporarily for any remaining call sites — will be removed.
 */
export const GUARDRAIL_ROLLING_OPTIONS: Record<
  TimeSeriesUnit | PointInTimeUnit,
  GuardrailRollingOption[]
> = {
  week:     [0, 2, 4, 8, 12],
  month:    [0, 2, 3, 6, 12],
  quarter:  [0, 2, 3, 4],
  semester: [0, 2, 3, 4],
};

export const GUARDRAIL_ROLLING_OPTION_LABELS: Record<
  GuardrailRollingOption,
  string
> = {
  0: "No Rolling Average",
  2: "2 Periods",
  3: "3 Periods",
  4: "4 Periods",
  6: "6 Periods",
  8: "8 Periods",
  12: "12 Periods",
  18: "18 Periods",
};

export const sortGuardrailRollingOptions = (
  options: readonly GuardrailRollingOption[],
): GuardrailRollingOption[] => {
  // Remove duplicates and sort numerically ascending (0 first, then 2/3/4/6/8/12).
  return Array.from(new Set(options)).sort((a, b) => a - b);
};

// ─── Viewer Time Options ─────────────────────────────────────────────

/** Rolling average config for a single viewer-selectable time unit (time-series viz). */
export interface TimeSeriesViewerUnitConfig {
  allowedRollingAverages: GuardrailRollingOption[];
  /** Applied when the viewer first switches to this unit. Must be in allowedRollingAverages. */
  defaultRollingAverage: GuardrailRollingOption;
}

/** Viewer time controls for Line Chart (time-series). */
export interface TimeSeriesViewerOptions {
  enabled: boolean;
  /** Extra RA options the researcher exposes for the chart's own configured unit (chart's RA always implicitly included). */
  chartUnitAdditionalRollingAverages: GuardrailRollingOption[];
  /** Additional time units beyond the chart's configured unit. */
  additionalUnits: Partial<Record<TimeSeriesUnit, TimeSeriesViewerUnitConfig>>;
}

/** Named period options viewers can select (excludes all_time and custom_period). */
export type ViewerSelectableTimePeriod =
  | "latest_month"
  | "previous_month"
  | "latest_quarter"
  | "previous_quarter"
  | "latest_semester"
  | "previous_semester";

/** Rolling average config for a single viewer-selectable named period (point-in-time viz). */
export interface PointInTimeViewerPeriodConfig {
  allowedRollingAverages: GuardrailRollingOption[];
  /** Applied when the viewer first switches to this period. Must be in allowedRollingAverages. */
  defaultRollingAverage: GuardrailRollingOption;
}

/** Viewer time controls for Bar Chart, Table, and Crosstab (point-in-time). */
export interface PointInTimeViewerOptions {
  enabled: boolean;
  /** Extra RA options the researcher exposes for the chart's own configured period (chart's RA always implicitly included). */
  chartPeriodAdditionalRollingAverages: GuardrailRollingOption[];
  /** Additional named periods beyond the chart's configured period. */
  additionalPeriods: Partial<Record<ViewerSelectableTimePeriod, PointInTimeViewerPeriodConfig>>;
}

export const DEFAULT_TIME_SERIES_VIEWER_OPTIONS: TimeSeriesViewerOptions = {
  enabled: false,
  chartUnitAdditionalRollingAverages: [],
  additionalUnits: {},
};

export const DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS: PointInTimeViewerOptions = {
  enabled: false,
  chartPeriodAdditionalRollingAverages: [],
  additionalPeriods: {},
};

/** Default guardrails applied to datasets that have none saved yet — all units enabled, all PRD-allowed options allowed. */
export function buildDefaultDateGuardrails(): DateGuardrails {
  const tsUnit = (unit: TimeSeriesUnit): UnitGuardrail => ({
    enabled: true,
    allowedRollingAverages: [...TS_GUARDRAIL_ROLLING_OPTIONS[unit]],
    defaultRollingAverage: 0,
  });
  const pitUnit = (unit: PointInTimeUnit): UnitGuardrail => ({
    enabled: true,
    allowedRollingAverages: [...PIT_GUARDRAIL_ROLLING_OPTIONS[unit]],
    defaultRollingAverage: 0,
  });

  return {
    timeSeries: {
      defaultUnit: "quarter",
      units: {
        week: tsUnit("week"),
        month: tsUnit("month"),
        quarter: tsUnit("quarter"),
        semester: tsUnit("semester"),
      },
    },
    pointInTime: {
      units: {
        month: pitUnit("month"),
        quarter: pitUnit("quarter"),
        semester: pitUnit("semester"),
      },
    },
  };
}

/**
 * Backfills legacy saved guardrails that predate the Monthly point-in-time unit
 * (PRD Module 1) with a disabled Month policy, leaving Quarter/Semester untouched.
 * A dataset with no saved guardrails at all should use buildDefaultDateGuardrails()
 * instead — this only patches an existing, otherwise-valid saved record.
 */
export function normalizeDateGuardrails(guardrails: DateGuardrails): DateGuardrails {
  if (guardrails.pointInTime.units.month) return guardrails;
  return {
    ...guardrails,
    pointInTime: {
      units: {
        ...guardrails.pointInTime.units,
        month: {
          enabled: false,
          allowedRollingAverages: [...PIT_GUARDRAIL_ROLLING_OPTIONS.month],
          defaultRollingAverage: 0,
        },
      },
    },
  };
}

// ─── Audit (Module 3) ─────────────────────────────────────────────────────────

export type GuardrailConflictType =
  | "DISABLED_TIME_SERIES_UNIT"
  | "DISABLED_POINT_IN_TIME_UNIT"
  | "INVALID_TIME_SERIES_ROLLING_AVERAGE"
  | "INVALID_POINT_IN_TIME_ROLLING_AVERAGE";

export type GuardrailWidgetType =
  | "line"
  | "bar"
  | "grouped_bar"
  | "stacked_bar"
  | "table"
  | "crosstab"
  | "pie"
  | "donut";

export interface GuardrailConflict {
  dashboardId: string;
  dashboardName: string;
  tabName: string;
  widgetTitle: string;
  widgetType: GuardrailWidgetType;
  widgetTimeUnit?: string;
  conflictType: GuardrailConflictType;
}

export interface ValidateGuardrailsRequest {
  datasetId: string;
  dateGuardrails: DateGuardrails;
}

export interface ValidateGuardrailsResponse {
  totalImpactedWidgets: number;
  impactedWidgets: GuardrailConflict[];
}

export interface Dataset {
  name: string;
  surveyIds: string[];
  id: string;
  datasetVersionId?: number; // Dataset version ID (number from API)
  totalResponseCount: number;
  datasetStatus: DatasetStatus;
  datasetState: DatasetState;
  createdAt: string;
  updatedAt: string;
  surveys: AggregationSurvey[];
  researchContext?: string;
  dataAvailabilityDate?: string | null;
  dateGuardrails?: DateGuardrails | null;
  bases?: DatasetBase[] | null;
  metrics?: DatasetMetric[] | null;
  client: {
    clientType: string;
    id: string;
    name: string;
  } | null;
}

export enum DatasetStatus {
  QUEUED = "QUEUED",
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
}

export enum DatasetState {
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
}

export enum StatisticalSignificance {
  POSITIVE = 1,
  NEUTRAL = 0,
  NEGATIVE = -1,
}

export type DatasetStateFilter = "active" | "archived" | "all";

export interface CreateDatasetRequest {
  name: string;
  surveyIds: string[];
}

export interface PatchDatasetRequest {
  id: string;
  name?: string;
  addSurveys?: string[];
  removeSurveys?: string[];
  datasetState?: string;
  clientId?: string;
  researchContext?: string;
  dataAvailability?: { date: string | null };
  dateGuardrailsConfigJson?: string;
  baseConfigJson?: string;
  metricsConfigJson?: string;
}
export interface Flag {
  id: string;
  label: string;
  color: "PRIMARY" | "SECONDARY" | "SUCCESS" | "WARNING" | "ERROR" | "INFO";
}

export interface Survey {
  id: string;
  name: string;
}

export interface Variable {
  id: string;
  label: string;
  title: string;
  type: "TEXT" | "NUMBER" | "SINGLE" | "MULTIPLE" | "FLOAT";
  index: number;
  rowtitle?: string;
  coltitle?: string;
  hidden?: boolean;
}

export interface Value {
  id: string;
  label: string;
  value: number;
  title: string;
  hidden?: boolean;
}

export interface Question {
  id: string;
  label: string;
  type: "TEXT" | "NUMBER" | "SINGLE" | "MULTIPLE" | "FLOAT";
  title: string;
  flags: Flag[];
  includedSurveys: Survey[];
  rowVariables: Variable[];
  columnVariables: Variable[];
  values: Value[];
  hidden?: boolean;
  /** True totals, unaffected by a visibility filter narrowing the arrays above. */
  variablesCount?: number;
  valuesCount?: number;
}

// Reconciliation details response interface
export interface DatasetReconciliationDetailsResponse {
  id: string;
  totalQuestionCount: number;
  matchedQuestionCount: number;
  unmatchedQuestionCount: number;
  variablesCount: number;
  orphanedVariablesCount: number;
  valuesCount: number;
  totalResponseCount: number;
  totalSurveyCount: number;
}

// Questions list response interface
export interface DatasetQuestionsListResponse {
  questions: QuestionDetail[];
  pagination: PaginationMetadata;
}

/** Visibility filter for the dataset Questions tab (curate-content module). */
export type VisibilityFilter = "all" | "visible" | "hidden";

export const QUESTIONS_PAGE_SIZE_OPTIONS = [10, 15, 20] as const;
export type QuestionsPageSize = (typeof QUESTIONS_PAGE_SIZE_OPTIONS)[number];

export interface QuestionDetail {
  name: string;
  datasetElementId: string;
  responses: number;
  title: string;
  label: string;
  grouping: string;
  variablesCount: number;
  valuesCount: number;
  surveysCount: number;
  type: string;
  includedSurveys: Survey[];
  rowVariables: Variable[];
  columnVariables: Variable[];
  values: QuestionValue[];
  hidden?: boolean;
}

export interface QuestionValue {
  id: string;
  label: string;
  title: string;
  value: number;
  rowtitle?: string;
  coltitle?: string;
  hidden?: boolean;
}

/** A single dataset content object that can be hidden/shown (curate-content module). */
export type DatasetElementKind = "question" | "variable" | "value";

/** "show_question" restores a hidden question and every current child in one action. */
export type VisibilityActionType = "hide" | "show" | "show_question";

export interface VisibilityTarget {
  id: string;
  kind: DatasetElementKind;
}

export interface DatasetElementVisibilityUpdate {
  datasetElementId: string;
  isHidden: boolean;
}

export interface UpdateDatasetElementsVisibilityRequest {
  datasetId: string;
  updates: DatasetElementVisibilityUpdate[];
}

export interface UpdateDatasetElementsVisibilityResponse {
  updates: DatasetElementVisibilityUpdate[];
}

// Legacy interface - kept for backward compatibility
export interface DatasetQuestionsResponse {
  id: string;
  totalQuestionCount: number;
  matchedQuestionCount: number;
  variablesCount: number;
  orphanedVariablesCount: number;
  valuesCount: number;
  totalResponseCount: number;
  totalSurveyCount: number;
  datasetStatus: "QUEUED" | "PROCESSING" | "COMPLETED" | "FAILED";
  questions: Question[];
  orphanedVariables: Variable[];
}

// Paginated datasets list response
export interface DatasetsListResponse {
  datasets: Dataset[];
  pagination: PaginationMetadata;
}

// Pagination metadata interface
export interface PaginationMetadata {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// highly possible to change or be removed dependig on the future actual response
export interface DatasetQuestionsSearchResponse {
  datasetElementId: string;
  name: string;
  responses: number;
  title: string;
  label: string;
  grouping: "rows" | "cols";
  variablesCount: number;
  valuesCount: number;
  type: "single" | "multi" | null;
  hidden?: boolean;
}

// Paginated response for dataset questions search
export interface DatasetQuestionsPaginatedResponse {
  questions: DatasetQuestionsSearchResponse[];
  pagination: PaginationMetadata;
}

// Legacy types for backward compatibility
export interface LegacyQuestion {
  id: string;
  questionType: "Single" | "Multiple" | "Number" | "Float" | "Text";
  title: string;
  flags: Flag[];
  counts: {
    variables: number;
    values: number | string; // Can be "N/A"
    includedInSurveys: number;
  };
  // Expanded data (loaded when accordion is opened)
  surveys?: Survey[];
  variables?: Variable[];
  values?: Value[];
}

export interface QuestionsState {
  questions: Question[];
  loading: boolean;
  error: string | null;
  expandedQuestions: Set<string>;
}
export interface QuestionsProps {
  questions?: Question[];
  loading?: boolean;
  error?: string | null;
}
export interface DescriptiveMetric {
  valueMetric: string;
  value: number;
  cellN: number;
  baseN: number;
  suppressed: boolean;
  crossVariableLabel: string;
  timePeriodLabel: string | null;
  statSignificance: number;
}

export interface QuestionsListForChart {
  data: Array<{
    variableId: string;
    variableLabel: string;
    surveys: Array<{
      percentage: number;
      weightedCount: number;
      totalCount: number;
      rank: number | null;
      statSignificance?: StatisticalSignificance | null;
      descriptiveMetrics?: DescriptiveMetric[];
    }>;
  }>;
  question: {
    id: string;
    label: string;
    responseCount: number;
    name: string;
    questionType?: "Single" | "Multiple" | "Float" | "Number";
    crossQuestionId?: string | null;
    baseN?: number | null;
  };
  weightsApplied: boolean;
  responsesMinDate?: string;
  responsesMaxDate?: string;
  effectiveRollingPeriodCount?: number;
  rollingPeriodsWarningMessage?: string;
  comparisonRollingLabel?: string;
  missingRollingPeriodLabels?: string[];
}

export interface WeightConfigurationRequest {
  weightConfigurations: WeightConfiguration[];
}

export interface WeightConfiguration {
  configurationId?: string;
  surveyId?: string;
  datasetElementQuestionId: string;
  datasetElementVariableId: string;
}

export interface WeightConfigurationResponse {
  weightConfigurations: WeightConfigurationResult[];
}

export interface WeightConfigurationResult {
  configurationId: string;
  questionTitle: string;
  questionLabel: string;
  variableTitle: string;
  variableLabel: string;
  surveyId: string;
  surveyName: string;
  datasetElementQuestionId: string;
  datasetElementVariableId: string;
}

export interface UseWeightConfigurationSaveProps {
  datasetId: string;
  surveyWeightingData: SurveyWeightingItem[];
  questionMappings: Record<string, Record<string, string>>;
  variableMappings: Record<string, Record<string, string>>;
}
